import React, { useEffect, useState, useMemo, useRef } from "react";
import { useLocation, useHistory } from "react-router-dom";
import styled, { useTheme } from "styled-components";
import { Button, Badge } from "react-bootstrap";
import { MainContainer } from "pages/Main";
import { Breadcrumb } from "components";
import * as config from "config";
import { withApiContext, withLabelsContext, withUserContext } from "context";
import Loader from "components/Spinners/Loader";

const Page = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.bodyBg || "#f2f2f2"};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const StatusBadge = styled.span`
  padding: 4px 12px;
  border: 1px solid ${({ theme }) => theme.color3 || "#333"};
  border-radius: 4px;
  font-size: 14px;
  color: ${({ theme }) => theme.color3 || "#333"};
`;

const InfoBar = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  background: ${({ theme }) => theme.color7 || "#fff"};
  border-radius: 8px;
  padding: 16px 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  @media (max-width: 992px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }
  @media (max-width: 576px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
`;

const InfoItem = styled.div`
  text-align: center;
`;

const InfoLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.primary || "#0077b6"};
  margin-bottom: 4px;
`;

const InfoValue = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.color1 || "#272D36"};
`;

const AttemptSection = styled.div`
  background: ${({ theme }) => theme.color7 || "#fff"};
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

const AttemptHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const AttemptTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.color1 || "#272D36"};
  margin: 0;
`;

const FeedbackButton = styled(Button)`
  && {
    border-radius: 8px;
    padding: 8px 20px;
    font-weight: 500;
    background: transparent;
    border: 1px solid ${({ theme }) => theme.color5 || "#ccc"};
    color: ${({ theme }) => theme.color3 || "#333"};
  }
  &&:hover {
    background: ${({ theme }) => theme.color6 || "#f5f5f5"};
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.5fr 1.5fr 1fr;
  gap: 24px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: ${({ theme }) => theme.color7 || "#fff"};
  border: 1px solid ${({ theme }) => theme.color6 || "#eee"};
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
`;

const StatTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.color3 || "#666"};
  text-transform: uppercase;
  margin-bottom: 16px;
`;

/* Timeline Card */
const TimelineCard = styled(StatCard)`
  position: relative;
`;

const TimelineContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
`;

const TimelinePoint = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const TimelineDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${({ theme }) => theme.color1 || "#333"};
  margin-bottom: 8px;
`;

const TimelineLabel = styled.div`
  font-size: 11px;
  font-weight: 500;
  color: ${({ theme }) => theme.color3 || "#666"};
`;

const TimelineDate = styled.div`
  font-size: 10px;
  color: ${({ theme }) => theme.primary || "#0077b6"};
`;

const TimelineLine = styled.div`
  width: 2px;
  height: 60px;
  background: ${({ theme }) => theme.color1 || "#333"};
  margin: 8px 0;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(-30deg);
    width: 40px;
    height: 2px;
    background: ${({ theme }) => theme.color1 || "#333"};
  }
`;

/* Progress Donut */
const ProgressCard = styled(StatCard)``;

const DonutContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const DonutWrapper = styled.div`
  position: relative;
  width: 120px;
  height: 120px;
`;

const DonutCenter = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
`;

const DonutValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${({ theme }) => theme.color2 || "#001B4A"};
`;

const DonutLabel = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.color3 || "#666"};
`;

const LegendList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: ${({ theme }) => theme.color3 || "#666"};
`;

const LegendDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background: ${({ $color }) => $color};
`;

/* Bar Chart */
const AttemptChartCard = styled(StatCard)``;

const BarChartContainer = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 32px;
  height: 140px;
  padding-top: 16px;
`;

const BarGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Bar = styled.div`
  width: 48px;
  background: ${({ $color }) => $color};
  border-radius: 4px 4px 0 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  min-height: 24px;
`;

const BarLabel = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.color3 || "#666"};
  margin-top: 8px;
  text-align: center;
`;

/* Score Arc */
const ScoreCard = styled(StatCard)`
  align-items: center;
`;

const ScoreArcWrapper = styled.div`
  position: relative;
  width: 140px;
  height: 80px;
  overflow: hidden;
`;

const ScoreValue = styled.div`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
`;

const ScoreNumber = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: ${({ theme }) => theme.color2 || "#001B4A"};
`;

const ScoreTotal = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.color3 || "#666"};
`;

const FooterActions = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 24px;
`;

const SummaryButton = styled(Button)`
  && {
    border-radius: 8px;
    padding: 8px 20px;
    font-weight: 500;
    background: transparent;
    border: 1px solid ${({ theme }) => theme.color5 || "#ccc"};
    color: ${({ theme }) => theme.color3 || "#333"};
  }
`;

const DownloadButton = styled(Button)`
  && {
    border-radius: 8px;
    padding: 8px 20px;
    font-weight: 500;
    background: transparent;
    border: 1px solid ${({ theme }) => theme.color5 || "#ccc"};
    color: ${({ theme }) => theme.color3 || "#333"};
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const days = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
  const months = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
  const day = days[d.getDay()];
  const date = String(d.getDate()).padStart(2, "0");
  const month = months[d.getMonth()];
  let hours = d.getHours();
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  const mins = String(d.getMinutes()).padStart(2, "0");
  return `${day}, ${date} ${month} | ${String(hours).padStart(2, "0")}:${mins} ${ampm}`;
};

const StudentRubricAttended = (props) => {
  const theme = useTheme();
  const history = useHistory();
  const location = useLocation();
  const { ctx_api } = props;

  const rubrics = location.state?.rubrics;
  const [loading, setLoading] = useState(true);
  const [attemptData, setAttemptData] = useState(null);

  const testId = rubrics?.id ?? rubrics?.rubrics?.id ?? rubrics?.rubrics?.rubrics_assessment_id ?? null;
  const attendedDetailsLoadedRef = useRef(false);
  const lastTestIdRef = useRef(null);

  useEffect(() => {
    if (!ctx_api || !testId) {
      setLoading(false);
      return;
    }

    // Only call API if testId has changed or hasn't been loaded yet
    if (attendedDetailsLoadedRef.current && lastTestIdRef.current === testId) {
      return;
    }

    lastTestIdRef.current = testId;
    attendedDetailsLoadedRef.current = true;

    const fetchAttemptDetails = async () => {
      setLoading(true);
      try {
        const { rsp } = await ctx_api.privateRequest({
          cmd: "get_rubrics_student_attended_details",
          args: { rubric_assessment_id: String(testId) },
        });
        const payload = Array.isArray(rsp) ? rsp[0] : rsp;
        setAttemptData(payload);
      } catch (err) {
        console.error("Failed to fetch rubric attempt details", err);
        attendedDetailsLoadedRef.current = false;
      } finally {
        setLoading(false);
      }
    };

    fetchAttemptDetails();
  }, [ctx_api, testId]);

  // Use data directly from API response
  const testName = attemptData?.rubric_assessment_name ?? attemptData?.name ?? rubrics?.name ?? "Rubric Assessment";
  const subject = attemptData?.subject ?? attemptData?.curriculum_name ?? rubrics?.currTitle ?? rubrics?.currName ?? "";
  const startDate = attemptData?.start_date ?? rubrics?.rubrics?.start_date ?? rubrics?.date ?? "";
  const endDate = attemptData?.end_date ?? rubrics?.rubrics?.end_date ?? rubrics?.endDate ?? "";
  const attemptStartDate = attemptData?.attempt_start_date ?? "";
  const attemptEndDate = attemptData?.attempt_end_date ?? "";
  const attempts = 1; // Could be enhanced if API returns attempt count
  const totalQuestions = attemptData?.total_questions ?? 0;
  const attemptedCount = attemptData?.attempted_count ?? 0;
  const unattemptedCount = attemptData?.unattempted_count ?? 0;
  const progress = attemptData?.progress ?? 0;

  // For score - these would come from evaluation data if available
  const score = attemptData?.total_teacher_score?? 0;
  const totalMarks = attemptData?.total_marks ?? rubrics?.rubrics?.total_marks ?? 0;

  if (loading) {
    return (
      <MainContainer>
        <Loader width={50} padding="100px 0" />
      </MainContainer>
    );
  }

  console.log("attemptData", attemptStartDate, attemptEndDate);
  
  console.log("attemptData", attemptData);
  // Donut chart SVG
  const total = totalQuestions || 1;
  const attemptedPct = (attemptedCount / total) * 100;
  const unattemptedPct = (unattemptedCount / total) * 100;

  const donutRadius = 50;
  const donutCircumference = 2 * Math.PI * donutRadius;
  const attemptedDash = (attemptedPct / 100) * donutCircumference;
  const unattemptedDash = (unattemptedPct / 100) * donutCircumference;

  // Score arc
  const scoreArcRadius = 60;
  const scoreArcCircumference = Math.PI * scoreArcRadius;
  const scorePct = totalMarks ? (score / totalMarks) * 100 : 0;
  const scoreDash = (scorePct / 100) * scoreArcCircumference;

  // Bar heights (max 120px)
  const maxBarHeight = 120;
  const maxBarValue = Math.max(attemptedCount, unattemptedCount, 1);
  const attemptedHeight = (attemptedCount / maxBarValue) * maxBarHeight || 24;
  const unattemptedHeight = (unattemptedCount / maxBarValue) * maxBarHeight || 24;

  const buildSummary = () => ({
  testName,
  subject,
  startDate: formatDate(startDate),
  endDate: formatDate(endDate),
  attempt: attempts,
  totalMarks,
  // questions: attemptData?.questions || [], // must come from API
  totalQuestions,
  attemptedCount,
  unattemptedCount

});


  return (
    <MainContainer>
      <Page>
        <Breadcrumb
          list={[
            { title: "Student Rubrics", link: config.ROUTES.StudentRubrics },
            { title: testName, current: true },
          ]}
        />

        <Header>
          <div />
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 14, color: theme.color3 }}>Status:</span>
            <StatusBadge>Attended</StatusBadge>
          </div>
        </Header>

        <InfoBar>
          <InfoItem>
            <InfoLabel>Test Name</InfoLabel>
            <InfoValue>{testName}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Subject</InfoLabel>
            <InfoValue>{subject || "-"}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Start Date</InfoLabel>
            <InfoValue>{formatDate(startDate)}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>End Date</InfoLabel>
            <InfoValue>{formatDate(endDate)}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Number of Attempts</InfoLabel>
            <InfoValue>{attempts}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>Total Mark</InfoLabel>
            <InfoValue>{totalMarks}</InfoValue>
          </InfoItem>
        </InfoBar>

        <AttemptSection>
          <AttemptHeader>
            <AttemptTitle>Attempt : {attempts}</AttemptTitle>
            <FeedbackButton>View Feedback</FeedbackButton>
          </AttemptHeader>

          <StatsGrid>
            {/* Timeline */}
            <TimelineCard>
              <TimelineContent>
                <TimelinePoint>
                  <TimelineDot />
                  <TimelineLabel>Attempt Started on</TimelineLabel>
                  <TimelineDate>{formatDate(attemptStartDate)}</TimelineDate>
                </TimelinePoint>
                <TimelineLine />
                <TimelinePoint>
                  <TimelineDot />
                  <TimelineLabel>Attempt Ended on</TimelineLabel>
                  <TimelineDate>{formatDate(attemptEndDate)}</TimelineDate>
                </TimelinePoint>
              </TimelineContent>
            </TimelineCard>

            {/* Progress Donut */}
            <ProgressCard>
              <StatTitle>PROGRESS</StatTitle>
              <DonutContainer>
                <DonutWrapper>
                  <svg width="120" height="120" viewBox="0 0 120 120">
                    <circle
                      cx="60"
                      cy="60"
                      r={donutRadius}
                      fill="none"
                      stroke={theme.color6 || "#e0e0e0"}
                      strokeWidth="12"
                    />
                    <circle
                      cx="60"
                      cy="60"
                      r={donutRadius}
                      fill="none"
                      stroke={theme.color2 || "#001B4A"}
                      strokeWidth="12"
                      strokeDasharray={`${attemptedDash} ${donutCircumference}`}
                      strokeDashoffset="0"
                      transform="rotate(-90 60 60)"
                    />
                    <circle
                      cx="60"
                      cy="60"
                      r={donutRadius}
                      fill="none"
                      stroke={theme.primary || "#0077b6"}
                      strokeWidth="12"
                      strokeDasharray={`${unattemptedDash} ${donutCircumference}`}
                      strokeDashoffset={-attemptedDash}
                      transform="rotate(-90 60 60)"
                    />
                  </svg>
                  <DonutCenter>
                    <DonutValue>{totalQuestions}</DonutValue>
                    <DonutLabel>Total</DonutLabel>
                  </DonutCenter>
                </DonutWrapper>
                <LegendList>
                  <LegendItem>
                    <LegendDot $color={theme.color2 || "#001B4A"} />
                    Attempted {attemptedCount}
                  </LegendItem>
                  <LegendItem>
                    <LegendDot $color={theme.primary || "#0077b6"} />
                    Unattempted {unattemptedCount}
                  </LegendItem>
                  <LegendItem>
                    <LegendDot $color="#4DD0E1" />
                    Flagged 0
                  </LegendItem>
                </LegendList>
              </DonutContainer>
            </ProgressCard>

            {/* Attempt Bar Chart */}
            <AttemptChartCard>
              <StatTitle>ATTEMPT</StatTitle>
              <BarChartContainer>
                <BarGroup>
                  <Bar $color={theme.color2 || "#001B4A"} style={{ height: attemptedHeight }}>
                    {attemptedCount}
                  </Bar>
                  <BarLabel>Total<br />Attempted</BarLabel>
                </BarGroup>
                <BarGroup>
                  <Bar $color={theme.primary || "#0077b6"} style={{ height: unattemptedHeight }}>
                    {unattemptedCount}
                  </Bar>
                  <BarLabel>Unattempted</BarLabel>
                </BarGroup>
              </BarChartContainer>
            </AttemptChartCard>

            {/* Score Arc */}
            <ScoreCard>
              <StatTitle>SCORE</StatTitle>
              <ScoreArcWrapper>
                <svg width="140" height="80" viewBox="0 0 140 80">
                  <path
                    d="M 10 70 A 60 60 0 0 1 130 70"
                    fill="none"
                    stroke={theme.color6 || "#e0e0e0"}
                    strokeWidth="10"
                    strokeLinecap="round"
                  />
                  <path
                    d="M 10 70 A 60 60 0 0 1 130 70"
                    fill="none"
                    stroke={theme.color2 || "#001B4A"}
                    strokeWidth="10"
                    strokeLinecap="round"
                    strokeDasharray={`${scoreDash} ${scoreArcCircumference}`}
                  />
                </svg>
                <ScoreValue>
                  <ScoreNumber>{score}</ScoreNumber>
                  <ScoreTotal>out of {totalMarks}</ScoreTotal>
                </ScoreValue>
              </ScoreArcWrapper>
            </ScoreCard>
          </StatsGrid>

          <FooterActions>
            <SummaryButton onClick={() => history.push(config.ROUTES.StudentRubricResponse,{
              rubricAssessmentId: testId,
              summary: buildSummary()
             })}>View Summary</SummaryButton>
            {/* <DownloadButton>
              Download Summary
              <span>⬆</span>
            </DownloadButton> */}
          </FooterActions>
        </AttemptSection>
      </Page>
    </MainContainer>
  );
};

export default withApiContext(withUserContext(withLabelsContext(StudentRubricAttended)));

